/**
 * The plains hub (ลานศิลาเก้ากระบี่) as the 2026-10-04 client painted it (commit 9fbc89f0,
 * apps/client/src/render/terrain.ts `buildGround` and `buildProps`) — user, 2026-10-10: "take the map and the
 * scenery from that commit". On the plains only, the ground is painted pixel for pixel as it was (the plaza's
 * flagstones, the road, the stone tiles with their seams, meadow and bare earth where the biome noise says),
 * and the ground cover is the old one (bushes, flowers, short grass tufts; no tall grass, pebbles, ferns, no
 * detail pass or rim on the props). It all still goes through client0's ground chunks and GPU blit.
 */
import { hash2, LAIR_RADIUS, onRoad, PLAZA_RADIUS, valueNoise, type GameMap } from '@nine/shared';

type RGB = [number, number, number];
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

/** Whether a map is drawn in the classic look: the plains hub only. */
export const classicPlains = (m: GameMap): boolean => m.zone === 'plains';

/**
 * One ground pixel of the classic plains (9fbc89f0 `buildGround`): screen pixel (`x`, `y`) over map point
 * (`gx`, `gy`); `biome` is the countryside noise at the map point (terrain.ts `biomeAt`, unturned).
 */
export function classicGroundPixel(m: GameMap, x: number, y: number, gx: number, gy: number, biome: number): RGB {
  const i = Math.floor(gx);
  const j = Math.floor(gy);
  const fx = gx - i;
  const fy = gy - j;
  const n = valueNoise(x / 7, y / 3.5);
  const pd = Math.hypot(gx - m.plaza.x, gy - m.plaza.y);
  const ld = Math.hypot(gx - m.lair.x, gy - m.lair.y);
  let base: RGB;
  let road = false;
  let meadow = 0;
  if (pd < PLAZA_RADIUS) {
    base = pd > PLAZA_RADIUS - 0.65 ? [98, 90, 82] : (i + j) & 1 ? [152, 143, 128] : [142, 133, 120];
    // C0-LOOK P5: flagstones differ a little from each other and the paving drifts in value across the square.
    const dr = 1 + (hash2(i * 5 + 11, j * 9 + 2) - 0.5) * 0.12 + (valueNoise(x / 30 + 9, y / 15) - 0.5) * 0.14;
    base = [base[0] * dr, base[1] * dr, base[2] * dr * 1.01];
  } else if (onRoad(m, gx, gy)) {
    base = [126, 108, 88];
    road = true;
  } else {
    base = (i + j) & 1 ? [124, 116, 105] : [113, 105, 95];
    // Countryside: meadow where the biome noise is high, packed earth where it is low.
    // C0-LOOK P5: the stone-to-grass change is authored, not a smooth blend: an ordered-dither edge, a
    // worn earth apron on the stone side, a few blades poking onto the stone, a darker grass lip.
    const gRaw = Math.min(1, Math.max(0, (biome - 0.5) / 0.14));
    const dRaw = Math.min(1, Math.max(0, (0.32 - biome) / 0.1));
    const G: RGB = (i + j) & 1 ? [92, 124, 70] : [84, 116, 64];
    const D: RGB = [126, 106, 84];
    const bd = (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16;
    // A little per-tile and broad value drift so the stone never reads as a repeating pattern.
    const drift = 1 + (hash2(i * 7 + 3, j * 13 + 1) - 0.5) * 0.1 + (valueNoise(x / 36 + 5, y / 18) - 0.5) * 0.12;
    base = [base[0] * drift, base[1] * drift, base[2] * drift];
    const isGrass = gRaw > 0 && gRaw >= 0.08 + bd * 0.84;
    if (isGrass) {
      base = gRaw < 0.45 ? [G[0] * 0.88, G[1] * 0.9, G[2] * 0.88] : G; // the darker lip of the lawn
    } else if (gRaw > 0 || dRaw > 0) {
      const apron = gRaw > 0 ? 0.55 : 0;
      const dm = Math.max(apron, dRaw);
      base = [0, 1, 2].map((q) => base[q]! + (D[q]! - base[q]!) * dm) as RGB;
      if (gRaw > 0 && gRaw < 0.3 && hash2(x * 3, y * 7) > 0.88) base = [G[0] * 1.05, G[1] * 1.08, G[2] * 1.02];
    }
    meadow = gRaw;
  }
  let mul = n < 0.32 ? 1.1 : n > 0.66 ? 0.8 : n > 0.55 ? 0.9 : 1;
  mul += (hash2(x, y) - 0.5) * 0.06;
  if (!road) {
    // Tile seams fade out under grass.
    const seam = 1 - meadow * 0.85;
    if (fx < 0.07 || fy < 0.07) mul *= 1 - 0.26 * seam;
    else if (fx > 0.93 || fy > 0.93) mul *= 1 + 0.08 * seam;
  }
  if (meadow > 0.3 && hash2(x, y) > 0.86) mul *= 1.18; // grass blades catching light
  let r = base[0] * mul;
  let g = base[1] * mul;
  let b = base[2] * mul;
  if (ld < LAIR_RADIUS) {
    const t = 1 - ld / LAIR_RADIUS;
    r += 38 * t;
    g -= 14 * t;
    b -= 10 * t;
  }
  return [r, g, b];
}
